/* eslint-disable @typescript-eslint/no-explicit-any */

// Imports for external libraries go here.
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useApolloClient } from '@apollo/client';
import { EditableComponent } from '@adobe/aem-react-editable-components';

// Imports for internal (to the monorepo) libraries go here,
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { Button, PillCollection, TabComponent } from '@marriott/mi-ui-library';

// The closer the import is to the file the lower it should be in this list.
import { DEFAULT_FILTERS_ORDER, FILTERS_MAPPING, TAB_ICONS } from '../../modules/constants/constant';
import { SelectedFiltersBar } from './SelectedFiltersBar/SelectedFiltersBar';
import { SearchResultsFilterPanel } from '../../molecules/SearchResultsFilterPanel';
import { MapView } from '../../molecules/MapView';
import { SearchResultsComponentProps } from './SearchResultsComponent.types';
import { StyledSearchResultsComponent } from './SearchResultsComponent.styles';
import {
  phoenixLuxuryDatedSearchByGeoQueryMap,
  phoenixLuxuryDatedSearchByLocationMap,
} from '../../molecules/MapView/queries';
import {
  fetchHDData,
  FiltersPillSelection,
  getDescriptionByCode,
  getDisplayedFilters,
  getFilteredOrder,
  totalFilterCount,
  updateFiltersPayload,
  useViewport,
} from '../../modules/helpers';

// Use named rather than default exports.
export const SearchResultsComponent: FC<SearchResultsComponentProps> = props => {
  const client = useApolloClient();

  const clientEnvVars = useClientEnvVarsStore.getState().envVarsObject;
  const { ACCEPT_LANGUAGE: currentLocale, REQUEST_ID: requestID } = clientEnvVars;

  const viewPort = useViewport();

  const facetsLoadingOnce = useRef(false);

  // State now stores only the list of markers, defaulting to an empty array
  const [selectedTab, setSelectedTab] = useState('map');
  const [popupOpenState, setPopupOpenState] = useState<boolean>(false);
  // State for pills data - initially empty, will be populated by useEffect
  const [pillsData, setPillsData] = useState<any[]>([]);
  const [markers, setMarkers] = useState<any[]>([]);
  const [facets, setFacets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtersPayload, setFiltersPayload] = useState<any>(() => updateFiltersPayload(props));
  const [filtersCount, setFiltersCount] = useState<number>(0);
  const [brandsClicked, setBrandsClicked] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<any>([]);
  // const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [filterButtonClicked, setFilterButtonClicked] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('united-states');

  const mapRef = useRef<google.maps.Map | null>(null);

  // Function to toggle the active tab
  const toggleActiveComponents = (tabValue: string) => {
    setSelectedTab(tabValue);
  };

  const getData = async (query: any, variables: any) => {
    setIsLoading(true);
    const data = await fetchHDData(client, query, {
      variables: variables,
      context: {
        headers: {
          'x-request-id': requestID,
          'accept-language': currentLocale?.replace('_', '-'),
        },
      },
    });

    const searchResults =
      data?.search?.properties?.searchByLocation?.edges ?? data?.search?.properties?.searchByGeolocation?.edges ?? [];
    const searchResultsFacets =
      data?.search?.properties?.searchByLocation?.facets ?? data?.search?.properties?.searchByGeolocation?.facets ?? [];

    setMarkers(searchResults);
    setFacets(searchResultsFacets);
    setIsLoading(false);
  };

  useEffect(() => {
    const variables = {
      search: {
        options: { numberInParty: 1 },
        facets: {
          terms: [...filtersPayload],
        },
      },
      limit: 20,
      offset: 0,
      sort: null,
    };

    getData(phoenixLuxuryDatedSearchByLocationMap, variables);
  }, []);

  const handleCenterChange = useCallback(() => {
    const center = mapRef.current?.getCenter();
    const newLat = center?.lat();
    const newLng = center?.lng();

    const mapCenter = { lat: newLat, lng: newLng };

    searchThisArea(mapCenter);
    // if (center) {
    //   setMapCenter(prevCenter => {
    //     const newLat = center?.lat();
    //     const newLng = center?.lng();
    //     if (prevCenter?.lat !== newLat || prevCenter?.lng !== newLng) {
    //       return { lat: newLat, lng: newLng };
    //     }
    //     return prevCenter;
    //   });
    // }
  }, []);

  const searchThisArea = (mapCenter: any) => {
    const variables = {
      search: {
        latitude: mapCenter?.lat,
        longitude: mapCenter?.lng,
        distance: 80467.2,
        options: {
          numberInParty: 1,
        },
      },
      limit: 20,
      offset: 0,
      sort: null,
    };
    if (mapCenter) {
      getData(phoenixLuxuryDatedSearchByGeoQueryMap, variables);
    }
  };

  const handleRemoveFilter = (filterType: string, filterCode: string) => {
    const newFiltersPayload = filtersPayload?.map((filterGroup: { type: string; dimensions: Array<string> }) => {
      if (filterGroup?.type === filterType) {
        const newDimensions = filterGroup?.dimensions?.filter((code: string) => code !== filterCode);
        const payload = updateFiltersPayload(props);
        if (filterType === FILTERS_MAPPING.Brands && props?.brandTags?.length > 0 && newDimensions?.length === 0) {
          setBrandsClicked(false);
          return {
            ...filterGroup,
            dimensions: payload[0]?.dimensions ?? [],
          };
        } else if (
          filterType === FILTERS_MAPPING.Amenities &&
          props?.amenityTags?.length > 0 &&
          newDimensions?.length === 0
        ) {
          return {
            ...filterGroup,
            dimensions: payload[1]?.dimensions ?? [],
          };
        } else if (
          filterType === FILTERS_MAPPING.Property &&
          props?.propertyTypeTags?.length > 0 &&
          newDimensions?.length === 0
        ) {
          return {
            ...filterGroup,
            dimensions: payload[2]?.dimensions ?? [],
          };
        } else if (
          filterType === FILTERS_MAPPING.Activities &&
          props?.activityTags?.length > 0 &&
          newDimensions?.length === 0
        ) {
          return {
            ...filterGroup,
            dimensions: payload[3]?.dimensions ?? [],
          };
        }
        return {
          ...filterGroup,
          dimensions: newDimensions,
        };
      }
      return filterGroup;
    });

    // Calculate total filter count
    const filterCount = totalFilterCount(newFiltersPayload, brandsClicked);

    setFiltersCount(filterCount);
    setFiltersPayload(newFiltersPayload);

    const variables = {
      search: {
        options: { numberInParty: 1 },
        facets: {
          terms: [...newFiltersPayload],
        },
      },
      limit: 20,
      offset: 0,
      sort: null,
    };

    getData(phoenixLuxuryDatedSearchByLocationMap, variables);
  };

  const handlePillClick = (value: string) => {
    setFilterButtonClicked(value);
    setPopupOpenState(true);
  };

  // useEffect to populate pills data from filters prop

  const filterDataMap: any = {
    BRANDS: { label: props?.brands?.brandsLabel, value: 'Brands' },
    LEISURE_REGIONS: { label: props?.regions?.regionsLabel, value: 'Regions' },
    LOCATION: { label: props?.regions?.locationLabel, value: 'Location' },
    AMENITIES: { label: props?.amenities?.amenitiesLabel, value: 'Amenities' },
    ACTIVITIES: { label: props?.activities?.activitiesLabel, value: 'Activities' },
    PROPERTY_TYPES: { label: props?.propertiesLabel, value: 'Property' },
  };

  useEffect(() => {
    if (!facets || facets.length === 0) return;

    if (!facetsLoadingOnce.current) {
      facetsLoadingOnce.current = true;
    }

    const pillData = [
      {
        label: 'All Filters',
        value: 'AllFilters',
        isActive: filtersCount > 0,
        showIcon: true,
        iconClass: 'icon-dining-filter',
        count: filtersCount,
        custom_click_track_value: 'props?.allFiltersClickTrackValue',
        buttonClassName: 'd-flex align-items-center all-filters-button',
        id: 'all filters',
        className: 'custom_click_track',
      },
    ];

    if (viewPort !== 'viewPortL') {
      setPillsData(pillData);
    } else if (facetsLoadingOnce.current) {
      const filtersOrderList =
        props?.filtersOrderList?.length > 0
          ? getFilteredOrder(props, props?.filtersOrderList)
          : getFilteredOrder(props, DEFAULT_FILTERS_ORDER);
      const newPillsData = filtersOrderList?.map((pill: any) => {
        const filterName = pill?.filterName;
        const filterInfo = filterDataMap?.[filterName];
        const isSelected = FiltersPillSelection(filtersPayload, filterName, props, brandsClicked);
        const iconDirection = filterButtonClicked === filterInfo.value ? 'icon-arrow-up' : 'icon-arrow-down';

        const regionFacets = facets?.filter(
          (filterObj: { type: { enumCode: string } }) => 'LEISURE_REGIONS' === filterObj?.type?.enumCode
        );

        const iValue = getDescriptionByCode(regionFacets, filtersPayload[filtersPayload?.length - 1]?.dimensions[0]);
        return {
          label: filterName === 'LEISURE_REGIONS' ? iValue : filterInfo?.value,
          value: filterInfo?.value,
          isActive: isSelected,
          showIcon: true,
          iconClass: iconDirection,
          custom_click_track_value: '',
          buttonClassName: 'filter-pill',
          id: pill.filterName,
          className: 'custom_click_track',
        };
      });

      setPillsData(newPillsData);
    }
  }, [filtersCount, viewPort, filterButtonClicked, facets]);

  useEffect(() => {
    const filtersForDisplay =
      facets?.length > 0 && filtersCount > 0 && getDisplayedFilters(filtersPayload, facets, brandsClicked, props);

    setSelectedFilters(filtersForDisplay);
  }, [popupOpenState, filtersCount]);

  return (
    <StyledSearchResultsComponent className="container">
      <h2 className="t-tilte-m">{props?.headerText}</h2>
      <div className="mb-3 d-flex justify-content-between">
        {facets?.length > 0 && (
          <PillCollection
            onChange={value => handlePillClick(value)}
            customClass={clsx('pill-collection d-flex')}
            pills={pillsData}
          />
        )}
        <div className="d-none">
          <TabComponent
            selectedTab={selectedTab}
            tabList={props?.tabList}
            setSelectedTab={(value: string) => toggleActiveComponents(value)}
            tabContainerClass={`m-icon-tab-wrapper px-0 px-md-5`}
            customClass={`m-icon-tab-list m-switcher-tab-list mr-2`}
            iconClass={clsx('m-icon-tab-list-item m-switcher-tab-list-item col-sm-6 col-md-auto')}
            isSwitcherTabs={true}
            isIconTabs={true}
            tabIcon={TAB_ICONS}
            classTabsEnabled="icons-tabs-container"
            ariaRole="link"
          />
        </div>
      </div>
      {selectedFilters && selectedFilters?.length > 0 && (
        <SelectedFiltersBar filters={selectedFilters} onRemoveFilter={handleRemoveFilter} />
      )}

      {selectedTab === 'map' ? (
        <>
          <MapView
            markers={markers}
            handleCenterChange={handleCenterChange}
            isLoading={isLoading}
            mapRef={mapRef}
            ctaLabel={props?.ctaLabel}
          />
          <div className={clsx('text-center cta-container d-none')}>
            <Button
              className="m-button-gallery map-button"
              aria-label="Search this area of the map"
              buttonCopy="Search This Area"
              callback={searchThisArea}
            />
          </div>
        </>
      ) : (
        <div>List View is here...</div>
      )}

      {popupOpenState && markers?.length > 0 && (
        <SearchResultsFilterPanel
          filterProps={props}
          facets={facets}
          setFacets={setFacets}
          filtersPayload={filtersPayload}
          setFiltersPayload={setFiltersPayload}
          setFiltersCount={setFiltersCount}
          popupOpenState={popupOpenState}
          setPopupOpenState={setPopupOpenState}
          setIsLoading={setIsLoading}
          setMarkers={setMarkers}
          brandsClicked={brandsClicked}
          setBrandsClicked={setBrandsClicked}
          selectedRegion={selectedRegion}
          setSelectedRegion={setSelectedRegion}
          setFilterButtonClicked={setFilterButtonClicked}
          filterButtonClicked={filterButtonClicked}
          viewPort={viewPort}
          resetLabel={props?.resetLabel}
          doneLabel={props?.doneLabel}
        />
      )}
    </StyledSearchResultsComponent>
  );
};

export const SearchResultsConfig = {
  emptyLabel: 'SearchResultsComponent',
  isEmpty: () => true,
  resourceType: 'mi-aem-luxury-spa/components/content/map-clusters/v1/map-clusters',
};

//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SearchResultsComponentEditable = (props: any) => (
  <div data-testid="luxury-SearchResultsComponent" data-component-name="o-luxury-SearchResultsComponent">
    <EditableComponent config={SearchResultsConfig} {...props}>
      <SearchResultsComponent {...props} />
    </EditableComponent>
  </div>
);
